import React, { useState } from 'react';
import { UserProfile, Ethnicity, Gender } from '../../../app/domain/types';
import { ArrowRight, ChevronDown, Check, MapPin, Activity, Shield, Heart } from 'lucide-react';

interface OnboardingProps {
  onComplete: (profile: UserProfile) => void;
}

const Onboarding: React.FC<OnboardingProps> = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<Partial<UserProfile>>({
    name: '',
    email: '',
    age: 30,
    gender: Gender.Female,
    ethnicity: Ethnicity.Caucasian,
    location: '',
    heightCm: 170,
    weightKg: 70,
    conditions: [],
    medications: [],
    allergies: [],
    goal: 'Weight Loss',
    communityPoints: 125,
    communityRank: 'Bronze',
    currentLevel: 12,
    currentXP: 750,
    nextLevelXP: 1000,
    streakDays: 5
  });

  const [conditionInput, setConditionInput] = useState('');
  const conditionsList = ['Type 2 Diabetes', 'Pre-Diabetes', 'Obesity', 'Post-Bariatric', 'Hypertension', 'PCOS'];

  const handleNext = () => {
    if (step < 4) {
      setStep(step + 1);
    } else {
      onComplete(formData as UserProfile);
    }
  };

  const updateField = (key: keyof UserProfile, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const toggleCondition = (condition: string) => {
    const current = formData.conditions || [];
    if (current.includes(condition)) {
      updateField('conditions', current.filter(c => c !== condition));
    } else {
      updateField('conditions', [...current, condition]);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] dark:bg-[#0f0f0f] text-gray-800 dark:text-gray-200 relative overflow-hidden font-sans transition-colors duration-300">
      {/* Background Decor */}
      <div className="absolute top-[-20%] right-[-10%] w-[800px] h-[800px] bg-gradient-radial from-health-100/40 to-transparent opacity-60 blur-3xl dark:from-health-900/20" />
      <div className="absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] bg-gradient-radial from-trust-100/40 to-transparent opacity-60 blur-3xl dark:from-trust-900/20" />

      <div className="w-full max-w-2xl z-10 p-6">
        
        {/* Progress Bar */}
        <div className="mb-12">
            <div className="flex justify-between text-xs uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3 font-medium">
                <span className={step >= 1 ? 'text-health-600 dark:text-health-400' : ''}>Profile</span>
                <span className={step >= 2 ? 'text-health-600 dark:text-health-400' : ''}>Health</span>
                <span className={step >= 3 ? 'text-health-600 dark:text-health-400' : ''}>Details</span>
                <span className={step >= 4 ? 'text-health-600 dark:text-health-400' : ''}>Goals</span>
            </div>
            <div className="flex gap-2">
                {[1, 2, 3, 4].map(i => (
                    <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-700 ${step >= i ? 'bg-health-600 dark:bg-health-500' : 'bg-stone-200 dark:bg-stone-800'}`} />
                ))}
            </div>
        </div>

        <div className="glass-panel p-8 md:p-12 rounded-[2rem] shadow-glass border border-white/60 dark:border-white/10 relative overflow-hidden bg-white/60 dark:bg-stone-900/60 backdrop-blur-xl">
            
            {step === 1 && (
                <div className="animate-fade-in-up space-y-8">
                    <div>
                        <h2 className="text-3xl font-light tracking-tight text-gray-900 dark:text-white">Let's build your <span className="font-medium text-health-600 dark:text-health-400">Profile</span>.</h2>
                        <p className="text-gray-500 dark:text-gray-400 mt-2 font-light">We need this to calibrate your ethnicity-aware health metrics.</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="col-span-2">
                            <label className="block text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2 font-medium">Full Name</label>
                            <input 
                                type="text" 
                                value={formData.name}
                                onChange={(e) => updateField('name', e.target.value)}
                                className="w-full bg-stone-50/50 dark:bg-stone-800/50 border-b border-stone-200 dark:border-stone-700 focus:border-health-600 dark:focus:border-health-400 px-0 py-3 text-lg outline-none transition-colors placeholder-gray-300 dark:placeholder-gray-600 text-gray-900 dark:text-white"
                                placeholder="e.g. Sarah Mitchell"
                            />
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2 font-medium">Age</label>
                            <input 
                                type="number" 
                                value={formData.age}
                                onChange={(e) => updateField('age', parseInt(e.target.value))}
                                className="w-full bg-stone-50/50 dark:bg-stone-800/50 border-b border-stone-200 dark:border-stone-700 focus:border-health-600 dark:focus:border-health-400 px-0 py-3 text-lg outline-none transition-colors text-gray-900 dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2 font-medium">Gender</label>
                            <div className="relative">
                                <select 
                                    value={formData.gender}
                                    onChange={(e) => updateField('gender', e.target.value)}
                                    className="w-full bg-stone-50/50 dark:bg-stone-800/50 border-b border-stone-200 dark:border-stone-700 focus:border-health-600 dark:focus:border-health-400 px-0 py-3 text-lg outline-none appearance-none bg-transparent cursor-pointer text-gray-900 dark:text-white"
                                >
                                    {Object.values(Gender).map(g => <option key={g} value={g} className="bg-white dark:bg-stone-800 text-gray-900 dark:text-white">{g}</option>)}
                                </select>
                                <ChevronDown className="absolute right-0 top-4 text-gray-400 pointer-events-none" size={16} />
                            </div>
                        </div>
                        <div className="col-span-2">
                             <label className="block text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2 font-medium flex items-center gap-2">
                                Ethnicity <span className="text-xs normal-case text-health-600 dark:text-health-400 bg-health-50 dark:bg-health-900/30 px-2 py-0.5 rounded-full border border-health-100 dark:border-health-800">Critical for BMI</span>
                             </label>
                            <div className="relative">
                                <select 
                                    value={formData.ethnicity}
                                    onChange={(e) => updateField('ethnicity', e.target.value)}
                                    className="w-full bg-stone-50/50 dark:bg-stone-800/50 border-b border-stone-200 dark:border-stone-700 focus:border-health-600 dark:focus:border-health-400 px-0 py-3 text-lg outline-none appearance-none bg-transparent cursor-pointer text-gray-900 dark:text-white"
                                >
                                    {Object.values(Ethnicity).map(e => <option key={e} value={e} className="bg-white dark:bg-stone-800 text-gray-900 dark:text-white">{e}</option>)}
                                </select>
                                <ChevronDown className="absolute right-0 top-4 text-gray-400 pointer-events-none" size={16} />
                            </div>
                         </div>
                         <div className="col-span-2">
                            <label className="block text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2 font-medium">Location</label>
                            <div className="relative flex items-center group">
                                <MapPin className="text-gray-400 absolute left-0 group-focus-within:text-health-600 dark:group-focus-within:text-health-400 transition-colors" size={18} />
                                <input 
                                    type="text" 
                                    value={formData.location}
                                    onChange={(e) => updateField('location', e.target.value)}
                                    className="w-full bg-stone-50/50 dark:bg-stone-800/50 border-b border-stone-200 dark:border-stone-700 focus:border-health-600 dark:focus:border-health-400 pl-8 pr-8 py-3 text-lg outline-none transition-colors placeholder-gray-300 dark:placeholder-gray-600 text-gray-900 dark:text-white"
                                    placeholder="Enter city"
                                />
                                {/* Mock Autocomplete visual cue */}
                                {formData.location && (
                                    <div className="absolute right-0 flex items-center gap-2 pointer-events-none">
                                        <span className="text-gray-300 dark:text-gray-600 text-xs italic hidden md:block">Did you mean {formData.location}, USA?</span>
                                        <ChevronDown size={14} className="text-gray-400" />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className="animate-fade-in-up space-y-8">
                    <div>
                        <h2 className="text-3xl font-light tracking-tight text-gray-900 dark:text-white">Your <span className="font-medium text-health-600 dark:text-health-400">Health Data</span>.</h2>
                        <p className="text-gray-500 dark:text-gray-400 mt-2 font-light">Accurate measurements help us tailor your meal plans.</p>
                    </div>

                    <div className="grid grid-cols-2 gap-8">
                        <div>
                            <label className="block text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2 font-medium">Height (cm)</label>
                            <input 
                                type="number" 
                                value={formData.heightCm}
                                onChange={(e) => updateField('heightCm', parseInt(e.target.value))}
                                className="w-full bg-stone-50/50 dark:bg-stone-800/50 border-b border-stone-200 dark:border-stone-700 focus:border-health-600 dark:focus:border-health-400 px-0 py-3 text-3xl font-light outline-none transition-colors text-gray-900 dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2 font-medium">Weight (kg)</label>
                            <input 
                                type="number" 
                                value={formData.weightKg}
                                onChange={(e) => updateField('weightKg', parseInt(e.target.value))}
                                className="w-full bg-stone-50/50 dark:bg-stone-800/50 border-b border-stone-200 dark:border-stone-700 focus:border-health-600 dark:focus:border-health-400 px-0 py-3 text-3xl font-light outline-none transition-colors text-gray-900 dark:text-white"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-4 font-medium">Medical Conditions</label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {conditionsList.map(condition => (
                                <button
                                    key={condition}
                                    onClick={() => toggleCondition(condition)}
                                    className={`px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 border ${
                                        formData.conditions?.includes(condition)
                                        ? 'bg-health-600 text-white border-health-600 shadow-lg shadow-health-600/20'
                                        : 'bg-white dark:bg-stone-800 text-gray-600 dark:text-gray-300 border-gray-100 dark:border-stone-700 hover:border-gray-300 dark:hover:border-stone-500'
                                    }`}
                                >
                                    {condition}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {step === 3 && (
                <div className="animate-fade-in-up space-y-8">
                     <div>
                        <h2 className="text-3xl font-light tracking-tight text-gray-900 dark:text-white">Specific <span className="font-medium text-health-600 dark:text-health-400">Details</span>.</h2>
                        <p className="text-gray-500 dark:text-gray-400 mt-2 font-light">To keep you safe and ensure relevant advice.</p>
                    </div>

                    <div className="space-y-6">
                        <div>
                            <label className="block text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2 font-medium">Medications</label>
                            <div className="flex items-center gap-2 mb-2 p-3 bg-white/50 dark:bg-stone-800/50 border border-stone-200 dark:border-stone-700 rounded-xl focus-within:ring-2 focus-within:ring-trust-100 dark:focus-within:ring-trust-900/30 transition-shadow">
                                <Activity size={18} className="text-trust-500" />
                                <input 
                                    type="text" 
                                    className="bg-transparent w-full outline-none text-gray-700 dark:text-white placeholder-gray-400"
                                    placeholder="e.g. Metformin, Insulin"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            const val = e.currentTarget.value;
                                            if (val) updateField('medications', [...(formData.medications || []), val]);
                                            e.currentTarget.value = '';
                                        }
                                    }}
                                />
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {formData.medications?.map((m, i) => (
                                    <span key={i} className="px-3 py-1 bg-trust-50 dark:bg-trust-900/20 text-trust-600 dark:text-trust-400 rounded-full text-xs font-medium flex items-center gap-1 border border-trust-100 dark:border-trust-800">
                                        {m} <button onClick={() => updateField('medications', formData.medications?.filter((_, idx) => idx !== i))}><span className="text-trust-400 hover:text-trust-600 dark:hover:text-trust-300">×</span></button>
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2 font-medium">Allergies</label>
                            <div className="flex items-center gap-2 mb-2 p-3 bg-white/50 dark:bg-stone-800/50 border border-stone-200 dark:border-stone-700 rounded-xl focus-within:ring-2 focus-within:ring-energy-100 dark:focus-within:ring-energy-900/30 transition-shadow">
                                <Shield size={18} className="text-energy-500" />
                                <input 
                                    type="text" 
                                    className="bg-transparent w-full outline-none text-gray-700 dark:text-white placeholder-gray-400"
                                    placeholder="e.g. Peanuts, Gluten"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            const val = e.currentTarget.value;
                                            if (val) updateField('allergies', [...(formData.allergies || []), val]);
                                            e.currentTarget.value = '';
                                        }
                                    }}
                                />
                            </div>
                             <div className="flex flex-wrap gap-2">
                                {formData.allergies?.map((a, i) => (
                                    <span key={i} className="px-3 py-1 bg-energy-50 dark:bg-energy-900/20 text-energy-600 dark:text-energy-400 rounded-full text-xs font-medium flex items-center gap-1 border border-energy-100 dark:border-energy-800">
                                        {a} <button onClick={() => updateField('allergies', formData.allergies?.filter((_, idx) => idx !== i))}><span className="text-energy-400 hover:text-energy-600 dark:hover:text-energy-300">×</span></button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {step === 4 && (
                <div className="animate-fade-in-up space-y-8">
                     <div>
                        <h2 className="text-3xl font-light tracking-tight text-gray-900 dark:text-white">Your <span className="font-medium text-health-600 dark:text-health-400">North Star</span>.</h2>
                        <p className="text-gray-500 dark:text-gray-400 mt-2 font-light">What is your primary motivation?</p>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                        {['Weight Loss', 'Disease Management', 'Pre-Surgery Prep', 'Post-Surgery Maintenance'].map((goal) => (
                            <button 
                                key={goal}
                                onClick={() => updateField('goal', goal)}
                                className={`group w-full p-5 rounded-2xl border text-left flex justify-between items-center transition-all duration-300 ${
                                    formData.goal === goal 
                                    ? 'border-health-600 bg-health-50/50 dark:bg-health-900/20 text-health-800 dark:text-health-300 shadow-sm ring-1 ring-health-600/20' 
                                    : 'border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 hover:border-health-300 dark:hover:border-health-600 hover:shadow-md dark:text-gray-200'
                                }`}
                            >
                                <div className="flex items-center gap-4">
                                    <div className={`p-2 rounded-full ${formData.goal === goal ? 'bg-health-200 dark:bg-health-800 text-health-700 dark:text-health-300' : 'bg-stone-100 dark:bg-stone-700 text-gray-400 group-hover:text-health-500'}`}>
                                        <Heart size={20} />
                                    </div>
                                    <span className="font-medium text-lg">{goal}</span>
                                </div>
                                {formData.goal === goal && <div className="bg-health-600 text-white p-1 rounded-full"><Check size={14} /></div>}
                            </button>
                        ))}
                    </div>

                    <div className="bg-gradient-to-r from-trust-50 to-health-50 dark:from-trust-900/20 dark:to-health-900/20 p-4 rounded-xl border border-trust-100 dark:border-trust-800 flex items-start gap-3">
                        <div className="p-1.5 bg-white dark:bg-stone-800 rounded-full shadow-sm mt-0.5 text-trust-600 dark:text-trust-400">
                            <Shield size={16} />
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                            <p className="font-medium text-gray-900 dark:text-white mb-1">Privacy Guarantee</p>
                            Your health data is encrypted and never shared without your explicit consent.
                        </div>
                    </div>
                </div>
            )}

            <div className="mt-12 flex justify-between items-center">
                {step > 1 ? (
                    <button onClick={() => setStep(step - 1)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-sm font-medium px-4 py-2">
                        Back
                    </button>
                ) : <div></div>}
                
                <button 
                    onClick={handleNext}
                    className="flex items-center gap-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-8 py-3 rounded-full hover:bg-black dark:hover:bg-gray-100 transition-all shadow-lg shadow-gray-200 dark:shadow-stone-900/50 hover:shadow-xl hover:scale-105 active:scale-95 font-bold"
                >
                    {step === 4 ? 'Start Journey' : 'Next'}
                    <ArrowRight size={18} />
                </button>
            </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;